function envValue(key: string) {
  if (typeof process !== "undefined" && process.env?.[key]) return process.env[key]
  // In the browser bundle only VITE_-prefixed variables exist on
  // import.meta.env, so PUBLIC_STAGE resolves through its VITE_ mirror there —
  // without it, every stage-derived URL silently fell to its dev default
  // client-side (caught live: the sidebar's short-links link opened dev).
  return import.meta.env?.[key] ?? import.meta.env?.[`VITE_${key}`]
}

export { createTtlCache, type TtlCache, type TtlCacheOptions } from "./cache"
export {
  type DateInput,
  unixMsDate,
  utcDate,
  utcDateOnly,
  utcDayjs,
  utcIsoString,
  utcNow,
  utcObjectKeyTimestamp,
  utcPathParts,
  utcUnixMs,
} from "./date"
export { type LogContext, type LogLevel, logEvent } from "./logger"
export {
  isSupabaseConfigured,
  readSupabasePublicConfig,
  type SupabasePublicConfig,
  supabaseAuthIssuer,
  supabaseJwksUrl,
  supabaseProjectUrl,
} from "./supabase"

export function isDevStage(stage = envValue("PUBLIC_STAGE")) {
  return !stage || stage === "dev" || stage === "local"
}

export function dashboardDomain(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "dev") return "dev.dashboard.playbrain.com"
  if (stage === "staging") return "staging.dashboard.playbrain.com"
  if (stage === "prod") return "dashboard.playbrain.com"
  return "localhost:3000"
}

/**
 * Taiyoro's dashboard host for the current stage, so the launcher card does not
 * send someone working on dev across to production.
 */
export function taiyoroDashboardUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://dashboard.taiyoro.gg/"
  if (stage === "staging") return "https://staging.dashboard.taiyoro.gg/"
  return "https://dev.dashboard.taiyoro.gg/"
}

/**
 * The public Playbrain website for the stage — where a content record is
 * actually read. The dashboard links out to it so an editor can see a record
 * as a visitor sees it.
 */
export function publicSiteUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://playbrain.com"
  if (stage === "staging") return "https://staging.playbrain.com"
  return "https://dev.playbrain.com"
}

/**
 * PlayData's admin entry. PlayData is a Squarespace site rather than one of our
 * deployments, so there is a single URL for every stage and it lands on
 * Squarespace's own login when the visitor has no session there.
 */
export function playDataAdminUrl() {
  return "https://playdata.gg/config"
}

/**
 * The public sites behind the managed-site cards, so the launcher can offer the
 * site itself beside the CMS that edits it.
 *
 * Both follow the stage: someone working on dev who opens the site beside the
 * CMS they are editing wants dev's copy of it, not production's. Taiyoro runs
 * the three hosts its own deployment does; PlayData's public face is moving to
 * a Playbrain subdomain, which only production answers so far — the dev and
 * staging hosts are reserved for it and 404 until that work lands, which is
 * still the right address to offer from a dev dashboard.
 */
export function taiyoroSiteUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://taiyoro.gg"
  if (stage === "staging") return "https://staging.taiyoro.gg"
  return "https://dev.taiyoro.gg"
}

export function playDataSiteUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://playdata.playbrain.com"
  if (stage === "staging") return "https://staging.playdata.playbrain.com"
  return "https://dev.playdata.playbrain.com"
}

/**
 * The short-links (Kutt) UI host for the current stage, so the dashboard link
 * sends someone working on staging to staging rather than production.
 */
export function shortLinksUrl(stage = envValue("PUBLIC_STAGE")) {
  if (stage === "prod") return "https://links.playbrain.com/"
  if (stage === "staging") return "https://staging.links.playbrain.com/"
  return "https://dev.links.playbrain.com/"
}

/**
 * Whether the short-links app is actually deployed for the stage. All three
 * stages run the service now, so this guards only the local and preview stages
 * that have no host to call — the front-channel logout ping must not fire at
 * something that does not exist.
 */
export function shortLinksDeployed(stage = envValue("PUBLIC_STAGE")) {
  return stage === "dev" || stage === "staging" || stage === "prod"
}

export function dashboardUrl(stage = envValue("PUBLIC_STAGE")) {
  const domain = dashboardDomain(stage)
  return domain.startsWith("localhost") ? `http://${domain}/dashboard` : `https://${domain}`
}

export function dashboardLoginUrl(stage = envValue("PUBLIC_STAGE")) {
  const domain = dashboardDomain(stage)
  return domain.startsWith("localhost") ? `http://${domain}/login` : `https://${domain}/login`
}
